import PropTypes from 'prop-types';
import GlobalStyles from '@mui/material/GlobalStyles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import { nanoid } from 'nanoid';
import DoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { maxHeight } from '@mui/system';

const drawerWidth = 240;

const openedMixin = theme => ({
	width: drawerWidth,
	height: maxHeight,
	marginTop: '4rem',
	paddingBottom: '4rem',
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
});

const closedMixin = theme => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	marginTop: '4rem',
	paddingBottom: '4rem',
	width: `calc(${theme.spacing(7)} + 1px)`,
	height: maxHeight,
	[theme.breakpoints.up('sm')]: {
		width: `calc(${theme.spacing(8)} + 1px)`,
	},
});

const Drawer = styled(MuiDrawer, {
	shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
	width: drawerWidth,
	flexShrink: 0,
	boxSizing: 'border-box',
	border: 3,
	...(open && {
		...openedMixin(theme),
		'& .MuiDrawer-paper': openedMixin(theme),
	}),
	...(!open && {
		...closedMixin(theme),
		'& .MuiDrawer-paper': closedMixin(theme),
	}),
}));

/**
 * Left-oriented menu
 * @returns SidebarMenu component
 */
const SidebarMenu = ({ menu, open, onClick }) => {
	const generateMenu = () => {
		let tempMenu = [];
		//props.menu is an array of arrays.  Each array represents menu items and should be separated by a divider
		menu.forEach((m, index) => {
			if (m && m.length > 0 && m[0]) {
				tempMenu.push(m);
				if (menu.length - 1 > index) {
					tempMenu.push(<Divider key={nanoid()} />);
				}
			}
		});
		return tempMenu;
	};

	return (
		<Drawer variant='permanent' open={open}>
			<GlobalStyles
				styles={{
					'.MuiFab-root.MuiFab-root:not(.MuiSpeedDial-fab,.MuiSpeedDialAction-fab), .MuiSpeedDial--anchored':
						{
							top: '64px',
							left: open ? drawerWidth.toString() + 'px' : '64px',
						},
				}}
			/>
			<Divider />
			<List onClick={() => onClick(true)}>{generateMenu()}</List>
			<Divider />
			{menu.length > 2 && (
				<Box
					sx={{
						padding: 0,
						display: 'flex',
						alignContent: 'end',
						flexWrap: 'wrap',
						flexGrow: 2,
					}}
				>
					<Tooltip
						title={open ? 'Collapse' : 'Expand'}
						placement='top-end'
					>
						<IconButton
							onClick={onClick}
							sx={{
								display: 'flex',
								justifyContent: 'right',
								height: '3rem',
								width: '100%',
								borderRadius: 0,
								...(!open && { justifyContent: 'center' }),
							}}
						>
							<DoubleArrowRight
								sx={{
									color: '#757575',
									...(open && { rotate: '180deg' }),
								}}
							></DoubleArrowRight>
						</IconButton>
					</Tooltip>
				</Box>
			)}
		</Drawer>
	);
};

SidebarMenu.propTypes = {
	menu: PropTypes.arrayOf(PropTypes.array),
	open: PropTypes.bool,
	onClick: PropTypes.func,
};

export default SidebarMenu;
